import { useState, useEffect } from 'react';

interface VisitorStats {
  totalVisits: number;
  uniqueVisitors: number;
  lastVisit: string;
  isNewVisitor: boolean;
}

/**
 * Custom hook for tracking website visitors
 * Tracks both total page views and unique visitors using localStorage
 * Also sends data to Google Analytics if available
 */
export function useVisitorCounter() {
  const [stats, setStats] = useState<VisitorStats>({
    totalVisits: 0,
    uniqueVisitors: 0,
    lastVisit: '',
    isNewVisitor: false,
  });

  useEffect(() => {
    const VISITOR_KEY = 'janet_visitor_id';
    const STATS_KEY = 'janet_visitor_stats';
    const SESSION_KEY = 'janet_session_counted';

    // Check if this session has already been counted
    const sessionCounted = sessionStorage.getItem(SESSION_KEY);

    // Get or create unique visitor ID
    let visitorId = localStorage.getItem(VISITOR_KEY);
    const isNewVisitor = !visitorId;

    if (!visitorId) {
      // Generate unique ID for new visitor
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(VISITOR_KEY, visitorId);
    }

    // Get existing stats
    const existingStats = localStorage.getItem(STATS_KEY);
    let parsedStats: VisitorStats = {
      totalVisits: 0,
      uniqueVisitors: 0,
      lastVisit: '',
      isNewVisitor: false,
    };

    if (existingStats) {
      try {
        parsedStats = JSON.parse(existingStats);
      } catch (e) {
        console.error('Error parsing visitor stats:', e);
      }
    }

    // Only increment if this session hasn't been counted yet
    if (!sessionCounted) {
      // Increment total visits
      parsedStats.totalVisits += 1;

      // Increment unique visitors only for new visitor IDs
      if (isNewVisitor) {
        parsedStats.uniqueVisitors += 1;
      }

      // Update last visit timestamp
      parsedStats.lastVisit = new Date().toISOString();
      parsedStats.isNewVisitor = isNewVisitor;

      // Save updated stats
      localStorage.setItem(STATS_KEY, JSON.stringify(parsedStats));

      // Mark this session as counted
      sessionStorage.setItem(SESSION_KEY, 'true');

      // Send to Google Analytics if available
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_title: document.title,
          page_location: window.location.href,
          visitor_type: isNewVisitor ? 'new' : 'returning',
          visitor_id: visitorId,
        });

        // Track visitor type
        window.gtag('event', isNewVisitor ? 'new_visitor' : 'returning_visitor', {
          visitor_id: visitorId,
          timestamp: new Date().toISOString(),
        });
      }
    }

    setStats(parsedStats);
  }, []);

  return stats;
}

// Optional: Admin-only hook to reset counter
export function useResetVisitorCounter() {
  const resetCounter = () => {
    if (window.confirm('Are you sure you want to reset the visitor counter? This action cannot be undone.')) {
      localStorage.removeItem('janet_visitor_stats');
      sessionStorage.removeItem('janet_session_counted');
      window.location.reload();
    }
  };

  return resetCounter;
}

// Optional: Export stats for admin dashboard
export function getVisitorStats(): VisitorStats | null {
  const stats = localStorage.getItem('janet_visitor_stats');
  if (!stats) return null;
  
  try {
    return JSON.parse(stats);
  } catch (e) {
    console.error('Error parsing visitor stats:', e);
    return null;
  }
}